import translate from '@translate';
import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { setLocationParams } from 'src/resources/location/reducer';
import { RootStoreType } from 'src/types/store';
import { ReturnProductType, ReturnType } from 'src/types/return';
import { checkFormValidity, getEditData } from 'src/utils/formFunctions';
import Loading from 'src/components/Loading/intex';
import { checkPermission, useCheckPermission } from 'src/utils/checkPermission';
import TopBlock from './TopBlock';
import { EDIT_KEYS, EditKeysType, PROPERTY_EDIT_KEYS, STORAGE_KEY } from './constants';
import { addMessage } from 'src/resources/alertMessages/reducer';
import RenderComponents from './RenderComponents';
import {
    clearReturnDetail,
    confirmReturnDetailFetch,
    createReturnDetailFetch,
    editReturnDetailEshopNoteFetch,
    getReturnDetailChangelogFetch,
    getReturnDetailFetch,
    patchReturnDetailFetch,
    updateReturnDetailFetch
} from 'src/resources/returnDetail/reducer';
import { getAllowedStatuses, getDataFromOrder, loadOrderData, loadOrdersData } from './functions';
import { OptionType } from 'src/components/Select';
import PDFform from './PDFform';
import { OrderType } from 'src/types/order';
import ConfirmDialog, { ConfirmDialogStatePropsType } from 'src/components/ConfirmDialog';
import isDeepEqual from 'src/utils/isDeepEqual';
import { setStorageData } from 'src/resources/storage/reducer';
import { samePaths } from 'src/utils/checkPaths';
import { FormErrorsType, OnChangeType } from '../../types/global';
import api from '@api';
import {
    buildStatusCancelPatchPayload,
    isCancelStatusChange,
    RETURN_CANCEL_STATUS,
} from 'src/utils/cancelStatus';
import { SOURCE_SYS_WMS_SKLADON } from 'skladon-types';

const initData = { RETURNED_ORDER_PRODUCTS: [], FILES: [] };
const ReturnDetailContainer: FC = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();

    const { idReturn, idOrder } = useParams();

    const newReturn = !idReturn;

    const formRef = useRef<HTMLFormElement>(null);
    const productsFormRef = useRef<HTMLFormElement>(null);

    const data = useSelector((state: RootStoreType) => state.returnDetail.data);
    const changelog = useSelector((state: RootStoreType) => state.returnDetail.changelog);
    const loading = useSelector((state: RootStoreType) => state.returnDetail.loading);
    const productsData = useSelector((state: RootStoreType) => state.productsList.data);

    const returnStatusCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.returnStatusCodes);
    const returnStatusCodesObject = useSelector(
        (state: RootStoreType) => state.constantsCodes.objects.returnStatusCodes
    );
    const retunReasonCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.retunReasonCodes);
    const retunReasonCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.retunReasonCodes);
    const returnResolutionCodes = useSelector(
        (state: RootStoreType) => state.constantsCodes.data.returnResolutionCodes
    );
    const returnResolutionCodesObject = useSelector(
        (state: RootStoreType) => state.constantsCodes.objects.returnResolutionCodes
    );
    const boolNumberCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.boolNumberCodes);
    const boolNumberCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.boolNumberCodes);
    const lineStateCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.lineStateCodes);
    const sourceSysCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.sourceSysCodes);
    const sourceSysCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.sourceSysCodes);
    const transportPaidByCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.transportPaidByCodes);
    const transportPaidByCodesObject = useSelector(
        (state: RootStoreType) => state.constantsCodes.objects.transportPaidByCodes
    );
    const shipperCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.shipperCodes);
    const serviceCodes = useSelector((state: RootStoreType) => state.constantsCodes.data.serviceCodes);
    const skladonUser = useSelector((state: RootStoreType) => state.user.data?.SKLADON_USER);
    const orderTypeCodesObject = useSelector((state: RootStoreType) => state.constantsCodes.objects.orderTypeCodes);

    const locationHistory = useSelector((state: RootStoreType) => state.location.history);
    const storageData = useSelector((state: RootStoreType) => state.storage.data[STORAGE_KEY]);

    const [editData, setEditData] = useState<ReturnType>(getEditData(initData || {}, EDIT_KEYS));
    const [editErrors, setEditErrors] = useState<FormErrorsType>({});
    const [activeTab, setActiveTab] = useState<string>('detail');
    const [ordersOptions, setOrdersOptions] = useState<OptionType[]>([]);
    const [showPdfForm, setShowPdfForm] = useState<boolean>(false);
    const [orderData, setOrderData] = useState<OrderType | null>(null);
    const [loadingOrderData, setLoadingOrderData] = useState<boolean>(false);
    const [confirmParams, setConfirmParams] = useState<ConfirmDialogStatePropsType | null>(null);

    const permissionCreate = useCheckPermission('RETURN.CREATE');
    const permissionUpdate = useCheckPermission('RETURN.UPDATE');
    const permissionEditAll = useCheckPermission('RETURN.EDITALL');
    const permissionEditNote = useCheckPermission('RETURN.EDITNOTE');
    const permissionConfirm = useCheckPermission('RETURN.CONFIRM');
    const permissionEditStatus = data?.STATUS && checkPermission(`RETURN.STATUS.EDIT.${data.STATUS.toUpperCase()}`);

    const returnPropertyPermissions = useMemo(() => {
        const statusUpper = data?.STATUS?.toUpperCase();
        if (!statusUpper) {
            return {} as Record<string, boolean>;
        }
        return PROPERTY_EDIT_KEYS.reduce(
            (acc, key) => {
                acc[key] = checkPermission(`RETURN.PROPERTIES.${key}.EDIT.${statusUpper}`);
                return acc;
            },
            {} as Record<string, boolean>
        );
    }, [data?.STATUS]);

    const isDcixReturn = !newReturn && data?.SOURCE_SYS === SOURCE_SYS_WMS_SKLADON;
    const disableEdit =
        isDcixReturn || (newReturn ? !permissionCreate : !permissionEditStatus || !permissionUpdate);
    const isAttributeEditDisabled = (attributeKey: string) => {
        if (isDcixReturn) {
            return attributeKey !== 'FILES';
        }
        return Boolean(disableEdit && !permissionEditAll && !returnPropertyPermissions[attributeKey]);
    };
    const canEditAnyReturnPropertyInCurrentStatus =
        isDcixReturn || Object.values(returnPropertyPermissions).some(Boolean);

    const createReturnFromList = newReturn && location.pathname === '/returns/new/';

    const allowedStatuses = getAllowedStatuses(returnStatusCodes, returnStatusCodesObject, data);

    const backUrl = location.pathname.indexOf('orders') === 1 ? `/orders/${idOrder}/` : '/returns/';
    useEffect(() => {
        if (idReturn) {
            dispatch(setLocationParams({ name: translate('returnDetail.title', { idReturn }), backUrl }));
        } else {
            dispatch(setLocationParams({ name: translate('returnDetail.titleNew'), backUrl }));
        }
    }, [idReturn, backUrl, locationHistory]);

    // laod data orders if new order with order select
    useEffect(() => {
        if (createReturnFromList) {
            loadOrdersData(setOrdersOptions);
        }
    }, [createReturnFromList]);

    useEffect(() => {
        // Full order detail is only needed when creating a return from an order
        // (products mapping). Existing returns already include ORDER_* fields in GET.
        if (idOrder || createReturnFromList) {
            const orderIdToLoad = Number(idOrder || editData.ORDER_ID);
            if (orderIdToLoad) {
                loadOrderData(orderIdToLoad, setOrderData, setLoadingOrderData);
            }
        }
    }, [idOrder, createReturnFromList, editData.ORDER_ID]);

    useEffect(() => {
        if (idReturn) {
            dispatch(getReturnDetailFetch(idReturn));
        } else {
            setEditData(initData);
        }
        return () => {
            dispatch(clearReturnDetail());
        };
    }, [idReturn]);

    useEffect(() => {
        if (data) {
            setEditData(data);
        }

        if (!idReturn && data?.ID) {
            navigate('/returns/' + String(data.ID));
            setThisStorageData(null);
        }
    }, [data]);

    useEffect(() => {
        if (!changelog && activeTab === 'history') {
            dispatch(getReturnDetailChangelogFetch(idReturn));
        }
    }, [activeTab, changelog]);

    // update edit data after load order detail
    useEffect(() => {
        if ((idOrder || createReturnFromList) && orderData) {
            setEditData(getDataFromOrder(orderData, editData, skladonUser));
        }
    }, [orderData]);

    // save edit data -start
    const editDataRef = useRef<ReturnType>({});
    useEffect(() => {
        if (
            newReturn &&
            storageData &&
            // from receivings new or misclick and back 1 url
            (samePaths(locationHistory[0]?.pathname, '/returns') ||
                samePaths(locationHistory[1]?.pathname, '/returns/new'))
        ) {
            setConfirmParams({
                title: translate('returnDetail.confirmGetNewDataStorage'),
                content: translate('returnDetail.confirmGetNewDataStorageText'),
                action: () => {
                    setEditData(storageData);
                },
                onCancel: () => {
                    setThisStorageData(null);
                }
            });
        }
        return () => {
            if (!editDataRef.current?.ID) {
                if (isDeepEqual(editDataRef.current, initData)) {
                    setThisStorageData(null);
                } else {
                    setThisStorageData(editDataRef.current);
                }
            }
        };
    }, []);

    useEffect(() => {
        editDataRef.current = editData;
    }, [editData]);

    const setThisStorageData = (data: ReturnType | null) => {
        dispatch(setStorageData({ key: STORAGE_KEY, data }));
    };

    const onChange: OnChangeType<EditKeysType> = (key, value, error) => {
        const newData = { ...editData };
        const newErrors = { ...editErrors };
        newData[key] = value;
        newErrors[key] = error;
        setEditData(newData);
        setEditErrors(newErrors);
    };

    const onChangeProduct: OnChangeType<EditKeysType> = (key, value, error, errorKey) => {
        const newData = { ...editData };
        const newErrors = { ...editErrors };
        if (value.length) {
            delete newErrors.RETURNED_ORDER_PRODUCTS;
        } else {
            newErrors.RETURNED_ORDER_PRODUCTS = true;
        }
        newData[key] = value;
        if (errorKey) {
            newErrors[errorKey] = error;
        }
        setEditData(newData);
        setEditErrors(newErrors);
    };

    const handleConfirm = () => {
        dispatch(
            confirmReturnDetailFetch({
                idReturn,
                data: data
            })
        );
    };

    const handleSave = async () => {
        if (disableEdit) {
            if (canEditAnyReturnPropertyInCurrentStatus) {
                const partialPayload: Partial<ReturnType> = {};
                if (returnPropertyPermissions.FILES || isDcixReturn) {
                    partialPayload.FILES = editData.FILES;
                }
                if (editData.ESHOP_NOTE !== data?.ESHOP_NOTE) {
                    partialPayload.ESHOP_NOTE = editData.ESHOP_NOTE || '';
                }
                if (Object.keys(partialPayload).length) {
                    dispatch(
                        updateReturnDetailFetch({
                            idReturn,
                            data: partialPayload
                        })
                    );
                }
                return false;
            }
            if (permissionEditNote) {
                dispatch(
                    updateReturnDetailFetch({
                        idReturn,
                        data: { ...data, NOTE: editData.NOTE, ESHOP_NOTE: editData.ESHOP_NOTE }
                    })
                );
            } else {
                dispatch(editReturnDetailEshopNoteFetch({ idReturn, data: { ESHOP_NOTE: editData.ESHOP_NOTE || '' } }));
            }
            return false;
        }

        if (isCancelStatusChange(editData.STATUS, newReturn, RETURN_CANCEL_STATUS)) {
            dispatch(
                patchReturnDetailFetch({
                    idReturn,
                    data: buildStatusCancelPatchPayload(RETURN_CANCEL_STATUS),
                })
            );
            return false;
        }

        const newFormErrors: FormErrorsType = {};
        Object.assign(newFormErrors, checkFormValidity(formRef), checkFormValidity(productsFormRef));
        if (!editData?.RETURNED_ORDER_PRODUCTS?.length) {
            newFormErrors.RETURNED_ORDER_PRODUCTS = true;
        }

        if (!Object.keys(newFormErrors).length) {
            const formatData = {
                ...editData,
                RETURNED_ORDER_PRODUCTS: editData.RETURNED_ORDER_PRODUCTS?.filter(
                    (product) => !!Number(product.QUANTITY) || !!Number(product.RECEIVED)
                ).map((product) => {
                    const newProduct: ReturnProductType = { ...product };
                    if (newProduct.ID && newProduct.ID < 0) {
                        delete newProduct.ID;
                    }
                    return { ...newProduct, COUNT: String(newProduct.COUNT) };
                })
            };

            if (newReturn) {
                // Check if return already exists for this order
                if (editData.ORDER_ID) {
                    try {
                        const response = await api('get', `api/secure/returns/by-order/${editData.ORDER_ID}`);
                        const existingReturns = response.data.data;

                        if (existingReturns && existingReturns.length > 0) {
                            const rmaList = existingReturns.map((ret: any) => ret.RMA).join('\n');
                            const eshopOrderId = orderData?.ESHOP_ORDER_ID || '';

                            setConfirmParams({
                                title: translate('returnDetail.confirmReturnExists'),
                                content: translate('returnDetail.confirmReturnExistsText', {
                                    orderId: editData.ORDER_ID,
                                    eshopOrderId,
                                    rmaList
                                }),
                                action: () => {
                                    dispatch(createReturnDetailFetch(formatData));
                                    setConfirmParams(null);
                                },
                                onCancel: () => {
                                    setConfirmParams(null);
                                }
                            });
                            return false;
                        }
                    } catch (error) {
                        console.error('Error checking existing returns:', error);
                        // Continue with creation even if check fails
                    }
                }

                dispatch(createReturnDetailFetch(formatData));
            } else {
                dispatch(
                    updateReturnDetailFetch({
                        idReturn,
                        data: formatData
                    })
                );
            }
            return false;
        } else {
            dispatch(
                addMessage({
                    type: 'warning',
                    title: translate('messages.formWarningTitle'),
                    text: translate('messages.formWarningText')
                })
            );
            setEditErrors(newFormErrors);
            return true;
        }
    };

    return (
        <div data-testid="return-detail">
            {showPdfForm && <PDFform idReturn={idReturn} close={(): any => setShowPdfForm(false)} />}
            <TopBlock
                data={data}
                idReturn={idReturn}
                newReturn={newReturn}
                disableEdit={disableEdit}
                loading={!!loading}
                handleSave={handleSave}
                handleConfirm={handleConfirm}
                activeTab={activeTab}
                onChangeTab={(value) => setActiveTab(value)}
                permissionEditNote={permissionEditNote}
                permissionConfirm={permissionConfirm}
                showPdfForm={() => setShowPdfForm(true)}
            />

            {(loading || loadingOrderData) && <Loading />}
            <RenderComponents
                formRef={formRef}
                productsFormRef={productsFormRef}
                data={data}
                onChange={onChange}
                onChangeProduct={onChangeProduct}
                editData={editData}
                editErrors={editErrors}
                newReturn={newReturn}
                disableEdit={disableEdit}
                permissionEditAll={permissionEditAll}
                permissionEditNote={permissionEditNote}
                isAttributeEditDisabled={isAttributeEditDisabled}
                returnStatusCodes={returnStatusCodes}
                returnStatusCodesObject={returnStatusCodesObject}
                retunReasonCodes={retunReasonCodes}
                retunReasonCodesObject={retunReasonCodesObject}
                returnResolutionCodes={returnResolutionCodes}
                returnResolutionCodesObject={returnResolutionCodesObject}
                boolNumberCodesObject={boolNumberCodesObject}
                boolNumberCodes={boolNumberCodes}
                lineStateCodesObject={lineStateCodesObject}
                transportPaidByCodesObject={transportPaidByCodesObject}
                transportPaidByCodes={transportPaidByCodes}
                shipperCodes={shipperCodes}
                serviceCodes={serviceCodes}
                sourceSysCodesObject={sourceSysCodesObject}
                sourceSysCodes={sourceSysCodes}
                orderTypeCodesObject={orderTypeCodesObject}
                activeTab={activeTab}
                changelog={changelog}
                createReturnFromList={createReturnFromList}
                ordersOptions={ordersOptions}
                orderData={orderData}
                productsData={productsData}
                allowedStatuses={allowedStatuses}
                skladonUser={skladonUser}
                isDcixReturn={isDcixReturn}
            />

            {confirmParams && (
                <ConfirmDialog
                    title={confirmParams.title}
                    content={confirmParams.content}
                    customContent={confirmParams.customContent}
                    options={confirmParams.options}
                    optionsValue={confirmParams.optionsValue}
                    action={confirmParams.action}
                    onCancel={confirmParams.onCancel}
                    onClose={() => setConfirmParams(null)}
                />
            )}
        </div>
    );
};

export default ReturnDetailContainer;
